import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '../../../app/AppContext.jsx'

import { Button } from '../Button/index.js'
import { Close }  from '../icons/index.js'

import styles from './Modal.module.scss'

// ── Slot sub-components ────────────────────────────────────────────────────────

function Header({ children, onClose, closeLabel = 'Zamknij' }) {
  return (
    <div className={styles.header}>
      <div className={styles.titlePane}>
        {children}
      </div>
      {onClose && (
        <Button type="button" kind="icon" size="small" variant="ghost" onClick={onClose} aria-label={closeLabel}>
          <Close />
        </Button>
      )}
    </div>
  )
}
Header.displayName = 'Modal.Header'

function Content({ children, className }) {
  const cls = [styles.content, className].filter(Boolean).join(' ')
  return <div className={cls}>{children}</div>
}
Content.displayName = 'Modal.Content'

function Footer({ children }) {
  return <div className={styles.footer}>{children}</div>
}
Footer.displayName = 'Modal.Footer'

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Modal — centered overlay dialog for desktop.
 *
 * Renders via a portal into the current instance portal host inside the app root.
 * Size is determined by child content (no fixed width imposed here).
 *
 * Slot API: `Modal.Header`, `Modal.Content`, `Modal.Footer` — all optional.
 *
 * @param {object}           props
 * @param {boolean}          props.isOpen
 * @param {() => void}       props.onClose
 * @param {string}           [props.ariaLabel]
 * @param {string}           [props.className]        - Extra class on the outer centering panel
 * @param {string}           [props.dialogClassName]  - Extra class on the inner white dialog box
 * @param {React.ReactNode}  props.children
 */
export function Modal({ isOpen, onClose, ariaLabel, className, dialogClassName, children }) {
  const { portalHostId } = useApp()

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const portalTarget = document.getElementById(portalHostId)
  if (!portalTarget) return null

  return createPortal(
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={[styles.modalHolder, className].filter(Boolean).join(' ')}
      >
        <div className={[styles.dialog, dialogClassName].filter(Boolean).join(' ')}>
          {children}
        </div>
      </div>
    </>,
    portalTarget,
  )
}

Modal.Header  = Header
Modal.Content = Content
Modal.Footer  = Footer
